import { Component, StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.jsx'
import { supabase } from './db/index.js'
import { runPublicSiteHandoff } from './lib/publicSiteHandoff.js'

// ── The SHELL error boundary (2026-08-04) ────────────────────────────────────
// TabErrorBoundary (App.jsx) catches a crash inside a tab. Nothing caught a crash ABOVE
// it — in App itself, the header, the auth branch — and React's answer to an uncaught
// render error is to unmount the whole tree, i.e. a white page with no message. That is
// how a real bug became indistinguishable from a stale cache (owner, 2026-08-04).
//
// Deliberately plain inline styling and no i18n: this renders when the app did NOT come
// up, so it must not assume a theme, a font, or that the i18n module loaded at all.
class ShellErrorBoundary extends Component {
  state = { error: null }
  static getDerivedStateFromError(error) { return { error } }
  componentDidCatch(error, info) {
    console.error('[shell-error-boundary]', error, info?.componentStack)
    // Best-effort, and a FAILED import must not make the crash worse — this boundary is the
    // last one standing, so it loads the reporter dynamically and swallows everything.
    import('./lib/agentLog.js').then(m => m.logClientError('shell', error, info)).catch(() => {})
  }
  render() {
    if (!this.state.error) return this.props.children
    return (
      <div style={{ font: '14px/1.5 system-ui, sans-serif', padding: '2rem', textAlign: 'center', color: '#888' }}>
        <p style={{ margin: '0 0 .5rem' }}>აპლიკაცია ვერ ჩაიტვირთა.</p>
        <p style={{ margin: '0 0 1rem' }}>The app could not load.</p>
        {/* Same recovery as the boot guard, and for the same measured reason: a query string
            alone does not defeat iOS Safari's HTML cache (proven on a real iPhone
            2026-08-04), so the html is pulled through the network with cache:'reload'
            BEFORE navigating. Navigate regardless if that fetch fails or stalls. */}
        <button
          onClick={() => {
            try { sessionStorage.removeItem('sva-boot-retry') } catch { /* private mode */ }
            let went = false
            const go = () => {
              if (went) return
              went = true
              window.location.replace(window.location.pathname + '?r=' + Date.now() + (window.location.hash || ''))
            }
            try {
              fetch(window.location.pathname, { cache: 'reload', credentials: 'same-origin' }).then(go, go)
              setTimeout(go, 3000)
            } catch { go() }
          }}
          style={{ font: 'inherit', padding: '.5rem 1rem', border: '1px solid #888', borderRadius: '.5rem', background: 'none', color: 'inherit', cursor: 'pointer' }}>
          ხელახლა / Retry
        </button>
        <p style={{ margin: '1rem 0 0', fontSize: 12, opacity: 0.6 }}>{String(this.state.error?.message || this.state.error)}</p>
      </div>
    )
  }
}

// We got here, so the bundle loaded and the module graph is sound. Clear the boot guard's
// one-shot flag: without this the FIRST stale-chunk retry of a session would be the only
// one, and the next deploy that same day would land on a white page again.
try { sessionStorage.removeItem('sva-boot-retry') } catch { /* private mode */ }

const renderApp = () => createRoot(document.getElementById('root')).render(
  <StrictMode>
    <ShellErrorBoundary>
      <App />
    </ShellErrorBoundary>
  </StrictMode>,
)

// sentavus.com "Continue with senTavus Desk" (owner 2026-09-18) — see lib/publicSiteHandoff.js. On /public-login
// with a Desk session we leave at once and never render; otherwise the Desk renders as always (a failure here
// must never cost the Desk itself, so any error falls through to the normal render).
runPublicSiteHandoff(supabase).then(leaving => { if (!leaving) renderApp() }, () => renderApp())
